import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ListingCard from '../components/ListingCard';
import Avatar from '../components/ui/Avatar';
import Badge from '../components/ui/Badge';
import { Search, ArrowRight, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const LISTINGS_PER_PAGE  = 9;
const PROVIDERS_PER_PAGE = 10;

function ProfileRow({ profile }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/users/${profile.id}`)}
      className="flex items-center gap-4 px-5 py-4 bg-white border-b border-sand-100 last:border-0 hover:bg-sand-50 transition-colors duration-150 cursor-pointer group"
    >
      <Avatar
        src={profile.avatar_url}
        name={`${profile.first_name} ${profile.last_name}`}
        size="md"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="font-heading text-sm font-semibold text-ink-900">
            {profile.first_name} {profile.last_name}
          </span>
          <Badge variant="category" className="capitalize">{profile.role}</Badge>
          {profile.location && (
            <span className="flex items-center gap-1 text-xs text-ink-400">
              <MapPin size={11} />
              {profile.location}
            </span>
          )}
        </div>
        {profile.bio && (
          <p className="text-sm text-ink-400 line-clamp-1 leading-relaxed">
            {profile.bio}
          </p>
        )}
      </div>

      <ArrowRight
        size={15}
        className="text-ink-300 group-hover:text-terra-400 transition-colors duration-150 shrink-0"
      />
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= page - 1 && i <= page + 1)
    ) {
      pages.push(i);
    } else if (i === page - 2 || i === page + 2) {
      pages.push('…');
    }
  }
  const deduped = pages.filter((p, i) => p !== pages[i - 1]);

  return (
    <div className="flex justify-center items-center gap-1 mt-8">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="w-9 h-9 rounded-lg flex items-center justify-center bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
      >
        <ChevronLeft size={15} />
      </button>

      {deduped.map((p, i) =>
        p === '…' ? (
          <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-ink-400 text-sm">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={[
              'w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200',
              p === page
                ? 'bg-terra-400 text-white shadow-card'
                : 'bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50',
            ].join(' ')}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="w-9 h-9 rounded-lg flex items-center justify-center bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
      >
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

function EmptyState({ type, q }) {
  return (
    <div className="flex flex-col items-center py-20 gap-3 text-center">
      <div className="w-12 h-12 rounded-full bg-sand-100 flex items-center justify-center mb-1">
        <Search size={20} className="text-ink-300" />
      </div>
      <p className="font-heading text-lg font-semibold text-ink-900">No {type} found</p>
      <p className="text-sm text-ink-400 max-w-xs">
        {q
          ? `No ${type} matched "${q}". Try different keywords.`
          : 'Enter a search term above to find results.'}
      </p>
      {type === 'listings' && (
        <Link
          to="/listings"
          className="flex items-center gap-1 text-sm text-terra-400 hover:text-terra-500 transition-colors mt-2"
        >
          Browse all listings <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q   = searchParams.get('q')   || '';
  const tab = searchParams.get('tab') || 'listings';

  const [inputValue, setInputValue]       = useState(q);
  const [listings, setListings]           = useState([]);
  const [profiles, setProfiles]           = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [listingsPage, setListingsPage]         = useState(1);
  const [providersPage, setProvidersPage]       = useState(1);
  const [listingsTotalPages, setListingsTotalPages] = useState(1);
  const [listingsTotal, setListingsTotal]           = useState(0);

  // Fetch profiles + reset to page 1 whenever the query changes
  useEffect(() => {
    setInputValue(q);
    setListingsPage(1);
    setProvidersPage(1);

    if (!q.trim()) {
      setListings([]);
      setListingsTotalPages(1);
      setListingsTotal(0);
      setProfiles([]);
      return;
    }

    // Fetch page 1 of listings
    setListingsLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/listings`, {
        params: { search: q, limit: LISTINGS_PER_PAGE, page: 1 },
      })
      .then(res => {
        setListings(res.data.listings || []);
        setListingsTotalPages(res.data.totalPages || 1);
        setListingsTotal(res.data.total || 0);
      })
      .catch(() => { setListings([]); setListingsTotalPages(1); setListingsTotal(0); })
      .finally(() => setListingsLoading(false));

    setProfilesLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/users/search`, { params: { q } })
      .then(res => setProfiles(res.data.profiles || []))
      .catch(() => setProfiles([]))
      .finally(() => setProfilesLoading(false));
  }, [q]);

  // Fetch subsequent pages when user clicks pagination (page 1 is handled above)
  useEffect(() => {
    if (listingsPage === 1) return;
    if (!q.trim()) return;

    setListingsLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/listings`, {
        params: { search: q, limit: LISTINGS_PER_PAGE, page: listingsPage },
      })
      .then(res => {
        setListings(res.data.listings || []);
        setListingsTotalPages(res.data.totalPages || 1);
        setListingsTotal(res.data.total || 0);
      })
      .catch(() => { setListings([]); setListingsTotalPages(1); setListingsTotal(0); })
      .finally(() => setListingsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingsPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim(), tab });
    }
  };

  const setTab = (t) => setSearchParams({ q, tab: t });

  const loading = listingsLoading || profilesLoading;

  // listings is already the current page (server-paginates); profiles is client-paginated
  const providersTotalPages = Math.ceil(profiles.length / PROVIDERS_PER_PAGE);

  const pagedProfiles = profiles.slice(
    (providersPage - 1) * PROVIDERS_PER_PAGE,
    providersPage * PROVIDERS_PER_PAGE
  );

  return (
    <div className="bg-sand-50 min-h-[calc(100vh-4rem)]">

      {/* Search header */}
      <div className="bg-white border-b border-sand-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
              />
              <input
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="What do you need help with?"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-terra-400/25 focus:border-terra-400 transition-all duration-200 text-sm"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 bg-terra-400 text-white text-sm font-medium rounded-lg hover:bg-terra-500 transition-colors duration-200"
            >
              Search
            </button>
          </form>
          {q && (
            <p className="text-sm text-ink-400 mt-3">
              Results for <span className="font-medium text-ink-700">"{q}"</span>
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-sand-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex">
            {[
              { key: 'listings',  label: 'Listings',  count: listingsTotal },
              { key: 'providers', label: 'Companies', count: profiles.length },
            ].map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={[
                  'px-5 py-3.5 text-sm font-medium border-b-2 transition-all duration-200 flex items-center gap-2',
                  tab === t.key
                    ? 'border-terra-400 text-terra-400'
                    : 'border-transparent text-ink-400 hover:text-ink-700',
                ].join(' ')}
              >
                {t.label}
                {!loading && (
                  <span
                    className={[
                      'text-xs px-1.5 py-0.5 rounded-full font-medium',
                      tab === t.key
                        ? 'bg-terra-400/10 text-terra-400'
                        : 'bg-sand-100 text-ink-400',
                    ].join(' ')}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* ── Listings tab ── */}
        {tab === 'listings' && (
          listingsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(n => (
                <div key={n} className="bg-sand-100 animate-pulse rounded-xl h-72" />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <EmptyState type="listings" q={q} />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map(listing => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </div>
              <Pagination
                page={listingsPage}
                totalPages={listingsTotalPages}
                onChange={p => { setListingsPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
            </>
          )
        )}

        {/* ── Providers tab ── */}
        {tab === 'providers' && (
          profilesLoading ? (
            <div className="flex flex-col gap-0 bg-white border border-sand-200 rounded-lg shadow-card overflow-hidden">
              {[1, 2, 3, 4, 5].map(n => (
                <div key={n} className="flex items-center gap-4 px-5 py-4 border-b border-sand-100 last:border-0">
                  <div className="w-10 h-10 rounded-full bg-sand-200 animate-pulse shrink-0" />
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="h-3.5 bg-sand-200 animate-pulse rounded w-40" />
                    <div className="h-3 bg-sand-100 animate-pulse rounded w-64" />
                  </div>
                </div>
              ))}
            </div>
          ) : profiles.length === 0 ? (
            <EmptyState type="companies" q={q} />
          ) : (
            <>
              <div className="bg-white border border-sand-200 rounded-lg shadow-card overflow-hidden">
                {pagedProfiles.map(p => (
                  <ProfileRow key={p.id} profile={p} />
                ))}
              </div>
              <Pagination
                page={providersPage}
                totalPages={providersTotalPages}
                onChange={p => { setProvidersPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
            </>
          )
        )}

      </div>
    </div>
  );
}
